import { z } from 'zod';
import { DepartmentSchema, RoleGradeSchema } from './headcount';
/**
 * Manual known-departure credit (headcount planning D14).
 *
 * Managers enter a credit when they know turnover is coming that will not
 * show in roster data for 30-60 days. One credit = one departing head: from
 * effective_month forward, envelope surfaces subtract the credited monthly
 * cost (priced at the rate card when entered) from projected spend.
 *
 * Two credit types:
 * - 'departure': the head is leaving for good. Open-ended — applies until
 *   resolved (visible in roster actuals) or cancelled.
 * - 'leave': the head returns on a known date. end_month is required and the
 *   credit expires on its own: it applies for effective_month <= m < end_month
 *   (end_month is the return month — the first month the cost is back).
 *
 * No automatic attrition credit exists by design (D14) — this is deliberate,
 * manager-entered knowledge. Credits price a role at the rate card, never at
 * individual compensation. A credit MAY carry the departing person's
 * employee_id (D33 — the system_users doc id, the same identifier class D3
 * programs and D24 audit stamps use) so it can be deduped and matched against
 * HR data; the display name resolves at render. Keep names out of the
 * free-text note — the structured field is the person reference.
 *
 * Lifecycle: active → resolved (the departure is now visible in roster
 * actuals, so the credit must stop applying to avoid double-counting) or
 * cancelled (the departure is no longer expected). A leave credit past its
 * end_month simply stops applying — no one has to remember to resolve it.
 */
export const DEPARTURE_CREDIT_STATUSES = ['active', 'resolved', 'cancelled'];
export const DepartureCreditStatusSchema = z.enum(DEPARTURE_CREDIT_STATUSES);
export const DEPARTURE_CREDIT_TYPES = ['departure', 'leave'];
export const DepartureCreditTypeSchema = z.enum(DEPARTURE_CREDIT_TYPES);
const MONTH_PATTERN = /^\d{4}-(0[1-9]|1[0-2])$/;
/** Type/horizon consistency: a leave requires a return month after the
 * effective month; a departure forbids one. Shared by create and update. */
function refineCreditShape(data, ctx) {
    if (data.type === 'leave') {
        if (!data.end_month) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ['end_month'],
                message: 'A leave requires a return month',
            });
        }
        else if (data.end_month <= data.effective_month) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ['end_month'],
                message: 'The return month must be after the effective month',
            });
        }
    }
    else if (data.end_month !== null) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['end_month'],
            message: 'A departure is open-ended — no return month',
        });
    }
}
export const HiringDepartureCreditSchema = z
    .object({
    id: z.string().min(1),
    department: DepartmentSchema,
    program_id: z.string().nullable(),
    program_name: z.string().nullable(),
    role_grade: RoleGradeSchema,
    employee_id: z.string().min(1).nullable(),
    type: DepartureCreditTypeSchema,
    effective_month: z.string().regex(MONTH_PATTERN, 'effective_month must be YYYY-MM'),
    end_month: z.string().regex(MONTH_PATTERN, 'end_month must be YYYY-MM').nullable(),
    monthly_cost_credit: z.number().nonnegative(),
    rate_card_fiscal_year: z.number().int().nullable(),
    note: z.string(),
    status: DepartureCreditStatusSchema,
    created_at: z.string().min(1),
    created_by: z.string().min(1),
    created_by_name: z.string().min(1),
    updated_at: z.string().min(1),
    updated_by: z.string().min(1),
})
    .superRefine(refineCreditShape);
export const CreateHiringDepartureCreditDataSchema = z
    .object({
    department: DepartmentSchema,
    program_id: z.string().nullable(),
    program_name: z.string().nullable(),
    role_grade: RoleGradeSchema,
    employee_id: z.string().min(1).nullable(),
    type: DepartureCreditTypeSchema,
    effective_month: z.string().regex(MONTH_PATTERN, 'effective_month must be YYYY-MM'),
    end_month: z.string().regex(MONTH_PATTERN, 'end_month must be YYYY-MM').nullable(),
    note: z.string(),
})
    .superRefine(refineCreditShape);
